import { jsxs, jsx } from 'react/jsx-runtime';
import { isPresetGradientColor } from '@flex-design-system/fx-colors';
import { forwardRef } from 'react';
import { getColor } from '../utils/color.mjs';
import useId from './hooks/useId.mjs';
import { getID, FlexLinearGradient } from './shared/FlexLinearGradient.mjs';

const SignatureIcon = forwardRef(
  ({
    color: rawColor = "currentColor",
    size: sizeFromProps,
    type = "default",
    style,
    ...props
  }, forwardedRef) => {
    const { id } = useId();
    const isGradient = isPresetGradientColor(rawColor);
    const color = isGradient ? `url(#${getID(rawColor, id)})` : getColor(rawColor);
    const size = sizeFromProps ?? 24;
    if (type === "fill") {
      return /* @__PURE__ */ jsxs(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: size,
          height: size,
          fill: "none",
          viewBox: "0 0 24 24",
          ...props,
          ref: forwardedRef,
          style: sizeFromProps !== void 0 ? { width: size, height: size, flexShrink: 0, ...style } : { flexShrink: 0, ...style },
          children: [
            isGradient && /* @__PURE__ */ jsx(FlexLinearGradient, { id }),
            /* @__PURE__ */ jsx(
              "path",
              {
                fill: color,
                d: "M8.165 3.926c-.804-.262-1.64-.212-2.44.019-1.822.527-2.762 2.266-3.186 3.658a8.198 8.198 0 0 0-.355 2.083c-.01.308.004.61.049.88.039.237.122.585.345.873.343.441.98.523 1.424.184.416-.318.517-.894.247-1.33a2.662 2.662 0 0 1-.033-.546c.012-.435.096-.993.268-1.559C4.85 6.99 5.49 6.116 6.293 5.884c.515-.149.92-.143 1.24-.04.31.102.639.327.955.802.426.638.793 1.676 1.002 3.255-1.383.685-2.768 1.897-3.794 3.196-.566.716-1.051 1.494-1.376 2.276H2.851c-.606 0-1.101.487-1.101 1.094 0 .606.495 1.094 1.101 1.094h1.052c.014.19.043.379.092.566.505 1.96 2.74 2.578 4.488 1.786.93-.422 1.742-1.204 2.318-2.352h10.348c.606 0 1.101-.488 1.101-1.094 0-.607-.495-1.094-1.101-1.094H11.51c.128-.687.196-1.447.196-2.282 0-.659-.017-1.28-.052-1.864.619-.207 1.084-.334 1.43-.403a4.43 4.43 0 0 1 .24-.042v.04c0 .113-.027.346-.083.707l-.046.29v.002c-.041.254-.085.53-.117.777a6.847 6.847 0 0 0-.051.525 2.32 2.32 0 0 0 .03.57c.021.1.067.277.186.454.135.2.384.423.757.477.333.047.594-.067.729-.143.142-.08.252-.178.33-.254a22.034 22.034 0 0 1 2.208-1.917c.209-.154.403-.29.58-.402a16.05 16.05 0 0 0-.1.37 2.834 2.834 0 0 0-.103.77c.01.284.13.92.808 1.17.486.18.967.02 1.23-.082.325-.128.708-.335 1.154-.61.476-.296.622-.92.323-1.393a1.02 1.02 0 0 0-1.173-.425c.018-.097.032-.197.042-.297.024-.256.03-.641-.148-1.016a1.494 1.494 0 0 0-1.078-.825 1.774 1.774 0 0 0-.83.046 3.534 3.534 0 0 0-.648.26c-.407.208-.847.5-1.272.817-.24.177-.483.369-.724.566a4.16 4.16 0 0 0 .028-.437c0-.05.002-.085.004-.133a8.42 8.42 0 0 0 .004-.156 2.6 2.6 0 0 0-.028-.478 1.568 1.568 0 0 0-.277-.677 1.499 1.499 0 0 0-.708-.52c-.493-.18-1.076-.132-1.668-.014-.353.071-.765.18-1.246.33-.247-1.548-.665-2.758-1.255-3.644-.526-.788-1.203-1.339-2.017-1.604Zm-.87 10.418c.69-.875 1.538-1.658 2.365-2.208.01.305.014.623.014.955 0 .901-.089 1.655-.236 2.282H6.586c.199-.34.437-.686.708-1.029Zm-1.332 3.282a1.068 1.068 0 0 1-.015-.065h2.405a2.383 2.383 0 0 1-.713.515c-.943.427-1.574-.05-1.677-.45Z",
                fillRule: "evenodd",
                clipRule: "evenodd"
              }
            )
          ]
        }
      );
    }
    if (type === "default") {
      return /* @__PURE__ */ jsxs(
        "svg",
        {
          xmlns: "http://www.w3.org/2000/svg",
          width: size,
          height: size,
          fill: "none",
          viewBox: "0 0 24 24",
          ...props,
          ref: forwardedRef,
          style: sizeFromProps !== void 0 ? { width: size, height: size, flexShrink: 0, ...style } : { flexShrink: 0, ...style },
          children: [
            isGradient && /* @__PURE__ */ jsx(FlexLinearGradient, { id }),
            /* @__PURE__ */ jsx(
              "path",
              {
                fill: color,
                d: "M6.224 5.644c.549-.159 1.007-.16 1.386-.037.371.121.743.387 1.086.9.472.708.858 1.844 1.065 3.541-1.394.648-2.82 1.875-3.87 3.204-.591.75-1.09 1.563-1.4 2.368l-.001.003H2.85a.848.848 0 0 0-.851.844c0 .466.381.844.851.844h1.292c0 .255.03.507.094.753.454 1.762 2.488 2.371 4.143 1.62.906-.41 1.707-1.193 2.265-2.373h10.504c.47 0 .851-.378.851-.844a.847.847 0 0 0-.851-.844h-9.944c.162-.745.251-1.588.251-2.532 0-.726-.021-1.405-.064-2.039.716-.246 1.249-.394 1.643-.473.248-.05.425-.069.547-.07 0 .017 0 .041-.002.07a8.495 8.495 0 0 0-.005.243c0 .136-.032.39-.087.745l-.046.292c-.041.255-.085.528-.117.771a6.652 6.652 0 0 0-.049.505 2.08 2.08 0 0 0 .026.508c.018.085.055.225.149.364a.848.848 0 0 0 .585.37.902.902 0 0 0 .57-.114c.115-.064.208-.146.276-.213a22.277 22.277 0 0 1 2.236-1.94 7.631 7.631 0 0 1 1.019-.659l.093-.045a4.64 4.64 0 0 1-.075.321c-.016.064-.039.146-.063.232-.036.13-.075.271-.102.38a2.6 2.6 0 0 0-.096.7c.01.25.11.748.645.945.392.145.795.02 1.051-.081.305-.12.674-.317 1.114-.59a.756.756 0 0 0 .243-1.047.77.77 0 0 0-1.055-.242c-.1.062-.191.117-.275.166l.02-.08c.058-.215.118-.47.142-.723.023-.239.024-.57-.125-.885a1.244 1.244 0 0 0-.902-.687 1.525 1.525 0 0 0-.713.042 3.286 3.286 0 0 0-.602.243c-.39.198-.816.482-1.236.793-.402.298-.816.636-1.21.98l.01-.075c.052-.33.105-.703.105-.976 0-.055.002-.094.004-.142l.004-.152a2.36 2.36 0 0 0-.025-.432 1.317 1.317 0 0 0-.232-.57 1.249 1.249 0 0 0-.591-.434c-.425-.154-.951-.12-1.534-.002-.407.081-.9.216-1.498.412-.238-1.67-.662-2.935-1.26-3.833-.5-.75-1.134-1.26-1.886-1.505-.745-.243-1.53-.2-2.293.021-1.698.49-2.6 2.123-3.016 3.491a7.948 7.948 0 0 0-.345 2.018c-.01.297.004.581.046.831.037.225.111.522.296.76a.77.77 0 0 0 1.074.14.756.756 0 0 0 .168-1.027.788.788 0 0 1-.027-.118 2.904 2.904 0 0 1-.026-.54 6.446 6.446 0 0 1 .28-1.625c.373-1.224 1.05-2.203 1.979-2.471Zm3.41 9.979c.18-.686.29-1.522.29-2.532 0-.494-.01-.958-.03-1.396-.967.566-1.988 1.47-2.796 2.494a8.772 8.772 0 0 0-.934 1.434h3.47ZM5.674 17.31c.001.138.017.264.046.377.15.586.973 1.091 2.022.615.373-.169.78-.482 1.14-.992H5.676Z",
                fillRule: "evenodd",
                clipRule: "evenodd"
              }
            )
          ]
        }
      );
    }
    console.error(
      `SignatureIcon doesn't support the combination of ${size} and ${type}`
    );
    return null;
  }
);

export { SignatureIcon };
//# sourceMappingURL=SignatureIcon.mjs.map
